/**
 * Avaliador de expressões restrito, para o bloco de Condição.
 *
 * Por que não `eval`/`new Function`: a expressão vem de uma config pública e roda no navegador
 * de visitantes de sites de terceiros. Um interpretador próprio não tem acesso a `window`,
 * não chama função nenhuma e não lê propriedade de objeto nativo — o pior caso é um erro de
 * sintaxe, não execução de código arbitrário. É por isso que também não usamos `expr-eval`
 * (dependência parada e mais peso no bundle do widget do que estas ~200 linhas).
 *
 * Gramática suportada:
 *   expr       := or
 *   or         := and ( ('||' | 'or') and )*
 *   and        := cmp ( ('&&' | 'and') cmp )*
 *   cmp        := unary ( ('=='|'!='|'>'|'>='|'<'|'<='|'contains'|'notContains') unary )?
 *   unary      := ('!' | 'not') unary | primary
 *   primary    := '(' expr ')' | número | string | true | false | null | caminho.de.variavel
 */
import { getVariablePath, stringifyValue } from './interpolate.js';
export class ExpressionError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ExpressionError';
    }
}
const OPERATORS = ['&&', '||', '==', '!=', '>=', '<=', '>', '<', '!'];
const WORD_OPERATORS = new Set(['and', 'or', 'not', 'contains', 'notContains']);
const MAX_LENGTH = 1000;
function tokenize(input) {
    if (input.length > MAX_LENGTH)
        throw new ExpressionError('Expressão muito longa');
    const tokens = [];
    let i = 0;
    while (i < input.length) {
        const char = input[i];
        if (/\s/.test(char)) {
            i += 1;
            continue;
        }
        if (char === '(' || char === ')') {
            tokens.push({ type: 'paren', value: char });
            i += 1;
            continue;
        }
        if (char === '"' || char === "'") {
            const quote = char;
            let value = '';
            i += 1;
            while (i < input.length && input[i] !== quote) {
                if (input[i] === '\\' && i + 1 < input.length) {
                    value += input[i + 1];
                    i += 2;
                    continue;
                }
                value += input[i];
                i += 1;
            }
            if (i >= input.length)
                throw new ExpressionError('String não fechada na expressão');
            i += 1; // fecha a quote
            tokens.push({ type: 'string', value });
            continue;
        }
        if (/[0-9]/.test(char) || (char === '-' && /[0-9]/.test(input[i + 1] ?? ''))) {
            let value = char;
            i += 1;
            while (i < input.length && /[0-9.]/.test(input[i])) {
                value += input[i];
                i += 1;
            }
            tokens.push({ type: 'number', value });
            continue;
        }
        const twoChar = input.slice(i, i + 2);
        const matchedOp = OPERATORS.find((op) => op.length === 2 && op === twoChar);
        if (matchedOp) {
            tokens.push({ type: 'op', value: matchedOp });
            i += 2;
            continue;
        }
        if (OPERATORS.includes(char)) {
            tokens.push({ type: 'op', value: char });
            i += 1;
            continue;
        }
        if (/[A-Za-z_]/.test(char)) {
            let value = char;
            i += 1;
            while (i < input.length && /[A-Za-z0-9_.]/.test(input[i])) {
                value += input[i];
                i += 1;
            }
            tokens.push({ type: WORD_OPERATORS.has(value) ? 'op' : 'ident', value });
            continue;
        }
        throw new ExpressionError(`Caractere inesperado na expressão: "${char}"`);
    }
    return tokens;
}
// ---------------------------------------------------------------------------
// Comparação de valores
// ---------------------------------------------------------------------------
function asNumber(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed)
            return null;
        const n = Number(trimmed);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
export function isTruthy(value) {
    if (value === undefined || value === null)
        return false;
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value !== 0;
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        return v.length > 0 && v !== 'false' && v !== '0';
    }
    if (Array.isArray(value))
        return value.length > 0;
    return Object.keys(value).length > 0;
}
function looseEquals(a, b) {
    if (a === undefined || a === null || b === undefined || b === null) {
        const aEmpty = a === undefined || a === null || a === '';
        const bEmpty = b === undefined || b === null || b === '';
        return aEmpty && bEmpty;
    }
    if (typeof a === 'boolean' || typeof b === 'boolean') {
        return isTruthy(a) === isTruthy(b);
    }
    const na = asNumber(a);
    const nb = asNumber(b);
    if (na !== null && nb !== null)
        return na === nb;
    return stringifyValue(a) === stringifyValue(b);
}
function compare(a, b) {
    const na = asNumber(a);
    const nb = asNumber(b);
    if (na !== null && nb !== null)
        return na === nb ? 0 : na < nb ? -1 : 1;
    const sa = stringifyValue(a);
    const sb = stringifyValue(b);
    return sa === sb ? 0 : sa < sb ? -1 : 1;
}
/** `contains` é case-insensitive de propósito: é comparação de texto digitado em chat. */
function containsValue(haystack, needle) {
    if (Array.isArray(haystack)) {
        return haystack.some((item) => looseEquals(item, needle));
    }
    return stringifyValue(haystack).toLowerCase().includes(stringifyValue(needle).toLowerCase());
}
// ---------------------------------------------------------------------------
// Parser recursivo
// ---------------------------------------------------------------------------
class Parser {
    constructor(tokens, variables) {
        this.tokens = tokens;
        this.variables = variables;
        this.index = 0;
    }
    evaluate() {
        const value = this.parseOr();
        if (this.index < this.tokens.length) {
            throw new ExpressionError(`Sobrou "${this.tokens[this.index].value}" no fim da expressão`);
        }
        return isTruthy(value);
    }
    peek() {
        return this.tokens[this.index];
    }
    consumeOp(...values) {
        const token = this.peek();
        if (token && token.type === 'op' && values.includes(token.value)) {
            this.index += 1;
            return token.value;
        }
        return null;
    }
    parseOr() {
        let left = this.parseAnd();
        while (this.consumeOp('||', 'or')) {
            const right = this.parseAnd();
            left = isTruthy(left) || isTruthy(right);
        }
        return left;
    }
    parseAnd() {
        let left = this.parseComparison();
        while (this.consumeOp('&&', 'and')) {
            const right = this.parseComparison();
            left = isTruthy(left) && isTruthy(right);
        }
        return left;
    }
    parseComparison() {
        const left = this.parseUnary();
        const op = this.consumeOp('==', '!=', '>=', '<=', '>', '<', 'contains', 'notContains');
        if (!op)
            return left;
        const right = this.parseUnary();
        switch (op) {
            case '==':
                return looseEquals(left, right);
            case '!=':
                return !looseEquals(left, right);
            case '>':
                return compare(left, right) > 0;
            case '>=':
                return compare(left, right) >= 0;
            case '<':
                return compare(left, right) < 0;
            case '<=':
                return compare(left, right) <= 0;
            case 'contains':
                return containsValue(left, right);
            case 'notContains':
                return !containsValue(left, right);
            default:
                throw new ExpressionError(`Operador não suportado: ${op}`);
        }
    }
    parseUnary() {
        if (this.consumeOp('!', 'not')) {
            return !isTruthy(this.parseUnary());
        }
        return this.parsePrimary();
    }
    parsePrimary() {
        const token = this.peek();
        if (!token)
            throw new ExpressionError('Expressão incompleta');
        if (token.type === 'paren') {
            if (token.value !== '(')
                throw new ExpressionError('Parêntese ")" inesperado');
            this.index += 1;
            const value = this.parseOr();
            const closing = this.peek();
            if (!closing || closing.type !== 'paren' || closing.value !== ')') {
                throw new ExpressionError('Parêntese não fechado');
            }
            this.index += 1;
            return value;
        }
        this.index += 1;
        if (token.type === 'number') {
            const n = Number(token.value);
            if (!Number.isFinite(n))
                throw new ExpressionError(`Número inválido: ${token.value}`);
            return n;
        }
        if (token.type === 'string')
            return token.value;
        if (token.type === 'ident') {
            if (token.value === 'true')
                return true;
            if (token.value === 'false')
                return false;
            if (token.value === 'null')
                return null;
            const value = getVariablePath(this.variables, token.value);
            return value === undefined ? null : value;
        }
        throw new ExpressionError(`Token inesperado: "${token.value}"`);
    }
}
/** Avalia uma expressão booleana. Lança `ExpressionError` em sintaxe inválida. */
export function evaluateExpression(expression, variables) {
    const trimmed = (expression ?? '').trim();
    if (!trimmed)
        throw new ExpressionError('Expressão vazia');
    return new Parser(tokenize(trimmed), variables).evaluate();
}
/** Uma regra do modo builder — o caminho que a UI usa por padrão, sem sintaxe para o usuário. */
export function evaluateRule(rule, variables) {
    const left = getVariablePath(variables, rule.variable);
    const right = rule.value ?? '';
    switch (rule.operator) {
        case 'eq':
            return looseEquals(left, right);
        case 'neq':
            return !looseEquals(left, right);
        case 'gt':
            return compare(left, right) > 0;
        case 'gte':
            return compare(left, right) >= 0;
        case 'lt':
            return compare(left, right) < 0;
        case 'lte':
            return compare(left, right) <= 0;
        case 'contains':
            return containsValue(left, right);
        case 'notContains':
            return !containsValue(left, right);
        case 'isEmpty':
            return stringifyValue(left).trim().length === 0;
        case 'isNotEmpty':
            return stringifyValue(left).trim().length > 0;
        default:
            throw new ExpressionError(`Operador não suportado: ${String(rule.operator)}`);
    }
}
/** Avalia o bloco de condição nos dois modos. */
export function evaluateCondition(data, variables) {
    if (data.mode === 'expression') {
        return evaluateExpression(data.expression ?? '', variables);
    }
    const rules = data.rules ?? [];
    if (rules.length === 0)
        throw new ExpressionError('Condição sem regras');
    return data.combinator === 'or'
        ? rules.some((rule) => evaluateRule(rule, variables))
        : rules.every((rule) => evaluateRule(rule, variables));
}
//# sourceMappingURL=expression.js.map